'use client';

import { c as _c } from "react/compiler-runtime";
import type { QuickFilterPreset } from '@/components/filters/QuickFilters';
import { useQuickFilters, type UseQuickFiltersResult } from '@/lib/hooks/useQuickFilters';

/** Metadata key under which the Opportunities quick-filter presets are stored. */
export const OPPORTUNITIES_QUICK_FILTERS_METADATA_KEY = 'opportunities_quick_filters';

/**
 * Facet keys the Opportunities browser filters on. `categories` and `status`
 * are intentionally excluded: the page hard-codes categories and status is
 * admin-only, so presets target type/activities/occasions.
 */
const OPPORTUNITIES_QUICK_FILTER_FACET_KEYS: readonly string[] = ['type', 'activities', 'occasions'];

/**
 * Built-in default presets used when no `opportunities_quick_filters` metadata
 * is configured (or the stored value is invalid). Values are option ids from
 * the shared INITIATIVE_TYPE_CONFIG / INITIATIVE_TAG_OPTIONS /
 * INITIATIVE_OCCASION_OPTIONS constants.
 */
export const DEFAULT_OPPORTUNITIES_QUICK_FILTERS: readonly QuickFilterPreset[] = [{
  id: 'sampling-demos',
  label: 'Sampling & Demos',
  filters: {
    activities: ['product_sampling', 'product_demo', 'cooking_demo']
  }
}, {
  id: 'events',
  label: 'Events',
  filters: {
    type: ['event']
  }
}, {
  id: 'collaborations',
  label: 'Collaborations',
  filters: {
    type: ['collaboration']
  }
}, {
  id: 'promotions-giveaways',
  label: 'Promotions & Giveaways',
  filters: {
    type: ['promotion', 'giveaway', 'competition']
  }
}, {
  id: 'activations',
  label: 'Activations',
  filters: {
    type: ['activation', 'campaign']
  }
}, {
  id: 'workshops-courses',
  label: 'Workshops & Courses',
  filters: {
    type: ['workshop', 'course', 'webinar']
  }
}];

/**
 * Retrieve the Opportunities quick-filter presets. Thin wrapper around the
 * generic {@link useQuickFilters} that supplies the opportunities metadata key,
 * defaults and facet whitelist. Pass `{ optimistic: false }` to hold off
 * rendering until the server response settles.
 */
export function useOpportunitiesQuickFilters(t0) {
  const $ = _c(4);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  let t2;
  if ($[2] !== options.optimistic) {
    t2 = {
      metadataKey: OPPORTUNITIES_QUICK_FILTERS_METADATA_KEY,
      defaults: DEFAULT_OPPORTUNITIES_QUICK_FILTERS,
      allowedFacetKeys: OPPORTUNITIES_QUICK_FILTER_FACET_KEYS,
      optimistic: options.optimistic
    };
    $[2] = options.optimistic;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return useQuickFilters(t2);
}